import type { PendingAuthorization } from '@/utils/pkce'
import { clearIdToken, createPendingAuthorization, deriveChallenge, peekIdToken, peekPending, savePending, saveTarget } from '@/utils/pkce'
import { http, SKIP_REFRESH_HEADER } from '@/utils/request'

/**
 * 三端以「应用」身份接入（附录D）：与第三方应用走同一套授权码 + PKCE，
 * 不复用中心自己的登录表单。两个第一方客户端均为公共客户端（无 secret）+ 强制 PKCE。
 */
export const oidcConfig = {
  /** 个人中心客户端 */
  meClientId: import.meta.env.VITE_ME_CLIENT_ID ?? 'uc-me',
  /** 管理后台客户端 */
  adminClientId: import.meta.env.VITE_ADMIN_CLIENT_ID ?? 'uc-admin',
  redirectUri: import.meta.env.VITE_OIDC_REDIRECT_URI ?? `${window.location.origin}/callback`,
  /** 登出后回跳地址：必须与客户端注册的 post_logout_redirect_uris 逐字符相同，否则 SAS 直接拒绝 */
  postLogoutMe: import.meta.env.VITE_POST_LOGOUT_URI_ME ?? `${window.location.origin}/me`,
  postLogoutAdmin: import.meta.env.VITE_POST_LOGOUT_URI_ADMIN ?? `${window.location.origin}/admin`,
  scope: import.meta.env.VITE_OIDC_SCOPE ?? 'openid profile email',
  /** 中心地址：dev 为 http://localhost:9000（跨端口但同站）；生产与前端同部署域时为 '' */
  issuer: import.meta.env.VITE_UC_BASE ?? '',
}

/** `/oauth2/token` 的原始响应（OAuth2 规范形态，不包 R<T>）。 */
export interface TokenResponse {
  access_token: string
  token_type: string
  expires_in: number
  scope?: string
  id_token?: string
  /** SAS 1.5 不向公共客户端签发 refresh_token，故可缺失 */
  refresh_token?: string
}

/** OIDC `/userinfo`（SAS 内置端点，scope 含 openid 时可用）。 */
export interface OidcUserInfo {
  sub: string
  name?: string
  preferred_username?: string
  email?: string
}

export function buildAuthorizeUrl(pending: PendingAuthorization): string {
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: pending.clientId,
    redirect_uri: oidcConfig.redirectUri,
    scope: oidcConfig.scope,
    state: pending.state,
    code_challenge: pending.challenge,
    code_challenge_method: 'S256',
  })
  return `${oidcConfig.issuer}/oauth2/authorize?${params.toString()}`
}

/**
 * 发起授权：必须是**顶栏跳转**（附录D §1 明确禁止 XHR），
 * 因为中心要接管登录页与（开启时的）同意页。
 *
 * @param redirect 授权成功后回到的前端路径
 * @param target   发起方：`me` = 个人中心 uc-me；`admin` = 管理后台 uc-admin
 */
export async function startAuthorization(redirect: string, target: 'me' | 'admin' = 'me'): Promise<void> {
  const clientId = target === 'admin' ? oidcConfig.adminClientId : oidcConfig.meClientId
  const pending = await createPendingAuthorization(redirect, clientId)
  saveTarget(target)
  savePending(pending)
  window.location.assign(buildAuthorizeUrl(pending))
}

/**
 * 重新发起授权（不重新生成 state / verifier）：
 * SPA 登录页是授权码流程的登录页,用户契约登录成功后由它跳回 authorize;
 * 此时后端已同步建立授权会话,SAS 直接签发授权码 302 回 /callback。
 */
export async function restartAuthorization(): Promise<boolean> {
  const pending = peekPending()
  if (pending === null) {
    return false
  }
  // 重发请求不能用 pending.challenge（peekPending 不给它）：那样 code_challenge 会是空串，
  // 客户端开了 require-proof-key，中心直接回跳 error=invalid_request（副文案即"授权未完成"）
  const challenge = await deriveChallenge(pending.verifier)
  window.location.assign(buildAuthorizeUrl({ ...pending, challenge }))
  return true
}

/**
 * 发起 OIDC 全局登出（RP-Initiated，附录D §8）：整页跳 `/connect/logout`。
 *
 * <p>必须是整页跳转：登出要带走的是**中心的登录态**，那是 HttpSession 里的东西，
 * XHR 清不掉它；只清本地令牌的话，下次访问首页又会凭 SSO 会话静默登录回来。
 *
 * <p>{@code id_token_hint} 自 SAS 1.5 起是必填（缺失即 invalid_request），因此没有
 * id_token 时返回 {@code false} 交由调用方降级 —— 此时服务端的
 * {@code POST /api/auth/v1/logout} 已经销毁了授权会话，用户不会被自动登录回去。
 *
 * @return 是否已发起整页跳转
 */
export function startLogout(target: 'me' | 'admin' = 'me'): boolean {
  const idToken = peekIdToken()
  if (idToken === null) {
    return false
  }
  const params = new URLSearchParams({
    id_token_hint: idToken,
    client_id: target === 'admin' ? oidcConfig.adminClientId : oidcConfig.meClientId,
    post_logout_redirect_uri: target === 'admin' ? oidcConfig.postLogoutAdmin : oidcConfig.postLogoutMe,
  })
  // 一次性：发出即弃，避免"退出后还拿旧 id_token 反复调登出端点"
  clearIdToken()
  window.location.assign(`${oidcConfig.issuer}/connect/logout?${params.toString()}`)
  return true
}

/** 用 code + verifier 换令牌；走 Vite/Nginx 的 `/oauth2` 反代，避免跨域。 */
export async function exchangeCode(code: string, verifier: string, clientId: string): Promise<TokenResponse> {
  const response = await http.post<TokenResponse>(
    '/oauth2/token',
    new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      redirect_uri: oidcConfig.redirectUri,
      client_id: clientId,
      code_verifier: verifier,
    }),
    {
      baseURL: '',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        // 换令牌期间还没有 access_token，禁止触发 401 刷新逻辑
        [SKIP_REFRESH_HEADER]: '1',
      },
    },
  )
  return response.data
}

export async function fetchUserInfo(): Promise<OidcUserInfo> {
  const response = await http.get<OidcUserInfo>('/userinfo', { baseURL: '' })
  return response.data
}
